import React, { useMemo, useState } from "react";
import { Card, CustomerUpdate, CustomerUpdateStatus } from "@/types/v2";
import { cn, firestoreToDateTime } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/Base/DropdownMenu";
import { Icon } from "@/components/Common/Icon";
import { useServiceAdvisors } from "@/hooks/shop/useServiceAdvisors";
import { CustomerUpdateTimelineEntry } from "./CustomerUpdateTimelineEntry";

export type CustomerUpdatesTimelineProps = {
  updates: CustomerUpdate[];
  card?: Card;
  className?: string;
  compact?: boolean;
  initiallyOpenCount?: number; // number of items expanded by default from the top
  showHeader?: boolean;
  onSelectUpdate?: (u: CustomerUpdate) => void;
  emptyState?: React.ReactNode;
  dataTestId?: string;
};

/**
 *
 * @param u
 */
function getPrimaryTimestamp(u: CustomerUpdate): Date | undefined {
  // Choose the most relevant timestamp based on status
  switch (u.status) {
    case CustomerUpdateStatus.COMPLETED:
      return firestoreToDateTime(u.completedAt);
    case CustomerUpdateStatus.SKIPPED:
      return firestoreToDateTime(u.skippedAt);
    case CustomerUpdateStatus.CANCELLED:
      return firestoreToDateTime(u.cancelledAt);
    case CustomerUpdateStatus.SNOOZED:
      // Show last snooze time if available else scheduled/pending/created
      if (u.snoozes && u.snoozes.length > 0) {
        const last = u.snoozes[u.snoozes.length - 1];
        return firestoreToDateTime(last.snoozedAt);
      }
      return (
        firestoreToDateTime(u.pendingAt) ||
        firestoreToDateTime(u.scheduledAt) ||
        firestoreToDateTime(u.createdAt)
      );
    case CustomerUpdateStatus.PENDING:
      return (
        firestoreToDateTime(u.pendingAt) ||
        firestoreToDateTime(u.scheduledAt) ||
        firestoreToDateTime(u.createdAt)
      );
    case CustomerUpdateStatus.SCHEDULED:
      return (
        firestoreToDateTime(u.scheduledAt) || firestoreToDateTime(u.createdAt)
      );
    case CustomerUpdateStatus.CREATED:
    default:
      return firestoreToDateTime(u.createdAt);
  }
}

/**
 *
 * @param root0
 * @param root0.updates
 * @param root0.card
 * @param root0.className
 * @param root0.compact
 * @param root0.initiallyOpenCount
 * @param root0.showHeader
 * @param root0.onSelectUpdate
 * @param root0.emptyState
 * @param root0.dataTestId
 */
export function CustomerUpdatesTimeline({
  updates,
  card,
  className,
  compact = true,
  initiallyOpenCount = 1,
  showHeader = true,
  onSelectUpdate,
  emptyState,
  dataTestId,
}: CustomerUpdatesTimelineProps) {
  const [dense, setDense] = useState(compact);
  const serviceAdvisors = useServiceAdvisors();

  const sorted = useMemo(() => {
    return [...(updates ?? [])].sort((a, b) => {
      const ad = getPrimaryTimestamp(a)?.getTime() ?? 0;
      const bd = getPrimaryTimestamp(b)?.getTime() ?? 0;
      return bd - ad; // newest first
    });
  }, [updates]);

  if (!sorted.length) {
    return (
      <div
        className={cn("text-sm text-muted-foreground", className)}
        data-testid={dataTestId}
      >
        {emptyState ?? <span>No customer updates yet.</span>}
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)} data-testid={dataTestId}>
      {showHeader && (
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-medium">
            {card ? (
              <span>Customer Updates for RO {card.cardNumber ?? card.id}</span>
            ) : (
              <span>Customer Updates</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center rounded-md border px-2 py-1 text-xs hover:bg-accent">
                  <Icon name="settings" className="mr-1 h-4 w-4" /> View
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Display</DropdownMenuLabel>
                <DropdownMenuCheckboxItem
                  checked={dense}
                  onCheckedChange={(v) => setDense(v)}
                >
                  Compact mode
                </DropdownMenuCheckboxItem>

                <DropdownMenuSeparator />
                <DropdownMenuItem disabled>
                  More options coming soon
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      <ul className="space-y-2">
        {sorted.map((u, idx) => (
          <CustomerUpdateTimelineEntry
            key={u.id}
            update={u}
            employees={serviceAdvisors}
            dense={dense}
            isOpenDefault={idx < initiallyOpenCount}
            onSelectUpdate={onSelectUpdate}
            showCardInfo={true}
          />
        ))}
      </ul>
    </div>
  );
}

export default CustomerUpdatesTimeline;
